import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import type { AxiosInstance } from "axios";
import { useQueryClient } from "react-query";
import { useApi } from "@/hooks/useApi";
import { useDashboardRouter } from "@/hooks/useDashboardRouter";
import { installWsConnectionTelemetry } from "@/lib/wsConnectionTelemetry";
import { isCodesmithPath } from "@/components/sidebar/useActiveApp";
import { ASSISTANT_SESSION_CACHE_TIME_MS, ASSISTANT_SESSION_STALE_TIME_MS, assistantSessionQueryKey, requestAssistantSession } from "./assistantSessionRequest";
import { beginHardSessionOpen, installSessionOpenTiming } from "./sessionOpenTiming";

// The org-less route redirects to the org one once orgs resolve; the session
// fetch takes no org, so it starts from either.
const SESSION_ROUTES = new Set(["/[org]/sessions/[[...sessionPath]]", "/sessions/[[...sessionPath]]"]);
export function deepLinkedSessionId(pathname: string, sessionPath: string | string[] | undefined): string | null {
  if (!SESSION_ROUTES.has(pathname)) return null;
  const firstSegment = Array.isArray(sessionPath) ? sessionPath[0] : sessionPath;
  const isDraft = firstSegment === "new";
  if (!firstSegment || isDraft) return null;
  return firstSegment;
}

// Same result as assistantSessionQuery's fetchAssistantSession, so the
// mounted query reuses it. The parser loads alongside the request instead of
// joining AuthProvider's root bundle.
async function fetchDeepLinkedSession(api: AxiosInstance, sessionId: string) {
  const [response, {
    parseSessionResponse
  }] = await Promise.all([requestAssistantSession(api, sessionId), import("./transformSession")]);
  return parseSessionResponse(response.data);
}

// Module-level because the React Compiler cannot lower `import()` inside a hook.
function preloadCodesmithChunk() {
  void import("./Codesmith");
}
function preloadTranscriptChunks() {
  void import("@/components/agent-session/SessionMarkdown");
  void import("@/components/agent-session/ToolCallDisplay");
  void import("@/components/agent-session/DiffViewer");
}

/**
 * Starts a Codesmith route's downloads at app mount, alongside `/api/user`:
 * the Codesmith chunk, and on a session URL its snapshot and transcript
 * chunks. The session endpoint authenticates by cookie and takes no org, so
 * nothing here waits on auth or org discovery; a 401/403 lands in the query
 * cache as an error and the mounted query refetches it.
 *
 * Runs once per page load: in-app navigations already have Codesmith
 * mounted, and its own query fetches on selection.
 */
export function usePrefetchDeepLinkedSession() {
  const $ = _c(6);
  const api = useApi();
  const queryClient = useQueryClient();
  const router = useDashboardRouter();
  const startedRef = useRef(false);
  const pathname = router.pathname;
  const sessionPath = router.query.sessionPath;
  let t0;
  let t1;
  if ($[0] !== api || $[1] !== pathname || $[2] !== queryClient || $[3] !== sessionPath) {
    t0 = () => {
      if (startedRef.current) {
        return;
      }
      startedRef.current = true;
      installSessionOpenTiming(api);
      installWsConnectionTelemetry(api);
      if (!isCodesmithPath(pathname)) {
        return;
      }
      preloadCodesmithChunk();
      const sessionId = deepLinkedSessionId(pathname, sessionPath);
      if (!sessionId) {
        return;
      }
      beginHardSessionOpen(sessionId);
      preloadTranscriptChunks();
      queryClient.prefetchQuery(assistantSessionQueryKey(sessionId), () => fetchDeepLinkedSession(api, sessionId), {
        staleTime: ASSISTANT_SESSION_STALE_TIME_MS,
        cacheTime: ASSISTANT_SESSION_CACHE_TIME_MS
      });
    };
    t1 = [api, queryClient, pathname, sessionPath];
    $[0] = api;
    $[1] = pathname;
    $[2] = queryClient;
    $[3] = sessionPath;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
}